<script setup lang="ts">
/**
 * @changelog
 *
 * 2026-03-11 (0.1.0) (jhoward)
 *   - Refactor to display account details, card images, and comp dollars using computed properties.
 *   - Remove inline offers rendering; replace with big links to offers, account, win/loss, and logout.
 *   - Add new layout and styles for account details.
 *
 * 2026-03-13 (0.1.1) (jhoward)
 *   - Show prompt to set up two-factor authentication if user has not enrolled any methods.
 *
 * 2026-03-17 (0.1.2) (jhoward)
 *   - Display account ID in account details and adjust CSS for name display.
 *
 * 2026-03-24 (0.2.0) (jhoward)
 *   - Add tier point thresholds and progress bar for next level.
 *   - Display percent to next tier and comp dollar info.
 *   - Add responsive layout and animation for progress bar.
 *
 * 2026-04-09 (0.2.1) (jhoward)
 *   - Fix tier progress calculation and add info dialog for tier points and comp dollars.
 *
 * 2026-06-02 (0.2.2) (jhoward)
 *   - Add property-specific passport card images.
 *   - Fix static asset loading for passport card images.
 *
 * 2026-09-09 (0.3.0) (jhoward)
 *   - Render the tournamentResults already loaded by getAccountInfo() through App\components\TournamentResults.vue.
 *
 * 2026-10-10 (0.4.0) (jhoward)
 *   - The page is now kept alive, so it no longer reads the account info again on every visit. useRevalidateOnActivate()
 *     refreshes it silently after ten minutes, and the local copies are reassigned because they do not follow the store.
 *   - Replace the "Let's Secure Your Account!" block - no link, and almost never shown since a verified email counts
 *     as an enrolled method - with PasskeyNudge, and add the AccountProtectionChecklist.
 *
 * 2026-10-21 (0.5.0) (jhoward)
 *   - Show PasswordSecurityBanner at the top of the page instead of it floating over every route.
 */

import PeppermillContentBlock from '@framework/components/layout/PeppermillContentBlock.vue';
import PeppermillBigLink from '@framework/components/PeppermillBigLink.vue';
import PeppermillDialog from '@framework/components/PeppermillDialog.vue';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import AccountProtectionChecklist from '@/components/AccountProtectionChecklist.vue';
import PeppermillViewport from '@/components/framework-proxy/PeppermillViewport.vue';
import PasskeyNudge from '@/components/PasskeyNudge.vue';
import PasswordSecurityBanner from '@/components/PasswordSecurityBanner.vue';
import TournamentResults from '@/components/TournamentResults.vue';
import type { OpcAccountInfo } from '@/defs/api/Accountinfo';
import { tierPointThresholds } from '@/defs/api/tierPointThresholds';
import type { OpcTournamentResult } from '@/defs/api/TournamentResult';
import getAccountInfo from '@/helpers/getAccountInfo';
import useRevalidateOnActivate from '@/helpers/useRevalidateOnActivate';
import useAppStore from '@/store';

const appStore = useAppStore();
const accountInfo = ref<OpcAccountInfo | null>(null);
const tournamentResultRows = ref<OpcTournamentResult[]>([]);
const isLoaded = ref<boolean>(false);
const isPointDetailsDialogOpen = ref<boolean>(false);
const currentTierIndex = computed<number | null>(() => {
  if (accountInfo.value) {
    const tierLevel = accountInfo.value.CASINO_PATRON_TIER_LEVEL.toLowerCase();
    const index = tierPointThresholds.findIndex((threshold) => threshold.name === tierLevel);
    return index !== -1 ? index : null;
  }
  return null;
});

const tierPointPercentToNextLevel = computed<number | null>(() => {
  if (accountInfo.value && currentTierIndex.value && currentTierIndex.value < tierPointThresholds.length - 1) {
    const nextTierThreshold = tierPointThresholds[currentTierIndex.value + 1].threshold;
    const currentTierThreshold = tierPointThresholds[currentTierIndex.value].threshold;
    const totalPointsForCurrentLevel = nextTierThreshold - currentTierThreshold;
    const pointsThroughCurrentLevel =
      totalPointsForCurrentLevel - accountInfo.value.CASINO_PATRON_POINTS_NEEDED_NEXT_TIER_LEVEL;
    return Math.max(0, Math.min((pointsThroughCurrentLevel / totalPointsForCurrentLevel) * 100, 100));
  }

  return null;
});

// const nextTierName = computed<string | null>(() => {
//   if (accountInfo.value && currentTierIndex.value !== null && currentTierIndex.value < tierPointThresholds.length - 1) {
//     return tierPointThresholds[currentTierIndex.value + 1].name;
//   }
//   return null;
// });

const nextTierClass = computed<{ [key: string]: boolean }>(() => {
  const output: { [key: string]: boolean } = {};
  if (accountInfo.value?.CASINO_PATRON_NEXT_TIER_LEVEL.length) {
    output['account_details--progress_bar-' + accountInfo.value?.CASINO_PATRON_NEXT_TIER_LEVEL.toLowerCase()] = true;
  }
  return output;
});

const accountCurrentCardLevel = computed<string | null>(() => {
  if (accountInfo.value) {
    const accountIdPrefix = accountInfo.value.CASINO_PATRON_ID.substring(0, 2);

    if ('2A' === accountIdPrefix) {
      return new URL(
        `../assets/passport-cards/rn/rewards_${accountInfo.value.CASINO_PATRON_TIER_LEVEL.toLowerCase()}.png`,
        import.meta.url,
      ).href;
    }

    if ('2B' === accountIdPrefix) {
      return new URL(
        `../assets/passport-cards/wv/rewards_${accountInfo.value.CASINO_PATRON_TIER_LEVEL.toLowerCase()}.png`,
        import.meta.url,
      ).href;
    }

    if ('2C' === accountIdPrefix) {
      return new URL(
        `../assets/passport-cards/wc/rewards_${accountInfo.value.CASINO_PATRON_TIER_LEVEL.toLowerCase()}.png`,
        import.meta.url,
      ).href;
    }
  }

  return null;
});
const accountNextCardLevel = computed<string | null>(() => {
  if (accountInfo.value && accountInfo.value.CASINO_PATRON_NEXT_TIER_LEVEL.length) {
    const accountIdPrefix = accountInfo.value.CASINO_PATRON_ID.substring(0, 2);

    if ('2A' === accountIdPrefix) {
      return new URL(
        `../assets/passport-cards/rn/rewards_${accountInfo.value.CASINO_PATRON_NEXT_TIER_LEVEL.toLowerCase()}.png`,
        import.meta.url,
      ).href;
    }

    if ('2B' === accountIdPrefix) {
      return new URL(
        `../assets/passport-cards/wv/rewards_${accountInfo.value.CASINO_PATRON_NEXT_TIER_LEVEL.toLowerCase()}.png`,
        import.meta.url,
      ).href;
    }

    if ('2C' === accountIdPrefix) {
      return new URL(
        `../assets/passport-cards/wc/rewards_${accountInfo.value.CASINO_PATRON_NEXT_TIER_LEVEL.toLowerCase()}.png`,
        import.meta.url,
      ).href;
    }
  }

  return null;
});
const accountCompDollars = computed<string | null>(() => {
  if (accountInfo.value) {
    return accountInfo.value.CASINO_PATRON_COMP_TOTAL.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  return null;
});

//Points and comp dollars move as the patron plays, so ten minutes. The local copies do not follow the store.
useRevalidateOnActivate(
  async () => {
    const loginData = await getAccountInfo(true);
    if (loginData) {
      accountInfo.value = loginData.accountInfo;
      tournamentResultRows.value = loginData.tournamentResults;
    }
  },
  10 * 60 * 1000,
);

let isLoadedTimer = -1;
onMounted(async () => {
  //getAccountInfo now returns account info and tournament results together, from the one getLoginData call.
  const loginData = await getAccountInfo();
  accountInfo.value = loginData?.accountInfo ?? null;
  tournamentResultRows.value = loginData?.tournamentResults ?? [];
  isLoadedTimer = setTimeout(() => {
    isLoaded.value = true;
  }, 500);
});

onUnmounted(() => {
  clearTimeout(isLoadedTimer);
});
</script>

<template>
  <peppermill-viewport>
    <password-security-banner></password-security-banner>
    <div
      class="account_details--container"
      :class="{ 'account_details--container-loaded': isLoaded }"
      v-if="accountInfo"
    >
      <div class="account_details--image">
        <img
          v-if="accountCurrentCardLevel"
          :alt="accountInfo.CASINO_PATRON_TIER_LEVEL + ' Card Level'"
          :src="accountCurrentCardLevel"
        />
        <img v-else alt="Card Level" src="@/assets/reward_tba.png" />
        <span class="account_details--name">
          {{ accountInfo.CASINO_PATRON_ID }}<br />
          {{ accountInfo.CASINO_PATRON_FULL_NAME }}
        </span>
      </div>

      <div class="account_details--next_level" v-if="accountInfo.CASINO_PATRON_NEXT_TIER_LEVEL.length">
        <div class="account_details--image">
          <img
            v-if="accountNextCardLevel"
            :alt="accountInfo.CASINO_PATRON_NEXT_TIER_LEVEL + ' Card Level'"
            :src="accountNextCardLevel"
          />
          <span class="account_details--name account_details--name-large"
            >{{ accountInfo.CASINO_PATRON_POINTS_NEEDED_NEXT_TIER_LEVEL }} Points to Next Level</span
          >
        </div>
      </div>

      <div class="account-details--info" v-if="accountInfo?.CASINO_PATRON_POINTS_NEEDED_KEEP_CURRENT_LEVEL">
        <p>
          Earn <em>{{ accountInfo?.CASINO_PATRON_POINTS_NEEDED_KEEP_CURRENT_LEVEL }}</em> tier points to keep your
          current tier level.
        </p>
      </div>
      <div
        class="account-details--info"
        v-else-if="
          tierPointPercentToNextLevel &&
          accountInfo?.CASINO_PATRON_NEXT_TIER_LEVEL.length &&
          tierPointPercentToNextLevel > 99
        "
      >
        <p>
          Visit the Passport Rewards Center to update your card to {{ accountInfo?.CASINO_PATRON_NEXT_TIER_LEVEL }}!
        </p>
      </div>
      <div
        class="account-details--info account_details--progress_bar"
        :class="nextTierClass"
        :style="{ '--percent-through-tier': tierPointPercentToNextLevel + '%' }"
        v-else-if="
          tierPointPercentToNextLevel &&
          accountInfo?.CASINO_PATRON_NEXT_TIER_LEVEL &&
          '2C' === accountInfo?.CASINO_PATRON_ID.substring(0, 2)
        "
      >
        <div class="account_details--progress_bar_text">
          {{ Math.round(tierPointPercentToNextLevel) }}% to {{ accountInfo?.CASINO_PATRON_NEXT_TIER_LEVEL }}
        </div>
        <div class="account_details--progress_bar_fill"></div>
      </div>

      <div class="account-details--info">
        <span>Current Tier: {{ accountInfo.CASINO_PATRON_TIER_LEVEL }}</span>
        <span
          >{{ accountInfo.CASINO_PATRON_POINT_TOTAL }} Tier Points
          <a
            class="material-icons"
            href="#"
            @click.prevent="isPointDetailsDialogOpen = true"
            title="Click to view details"
            >info</a
          ></span
        >
        <span v-if="accountCompDollars"
          >{{ accountCompDollars }} Comp Dollars
          <a
            class="material-icons"
            href="#"
            @click.prevent="isPointDetailsDialogOpen = true"
            title="Click to view details"
            >info</a
          ></span
        >
        <span v-if="accountInfo.CASINO_PATRON_NEXT_TIER_LEVEL.length"
          >Tier Points Needed for Next Tier: {{ accountInfo.CASINO_PATRON_POINTS_NEEDED_NEXT_TIER_LEVEL }}</span
        >
        <span></span>
      </div>
    </div>

    <h2>Tournament Results</h2>
    <tournament-results :results="tournamentResultRows"></tournament-results>

    <template v-if="appStore.state.new_pin_required">
      <h2>New PIN Required</h2>
      <peppermill-content-block>
        <p>You need to set/reset your PIN before we can show your account information.</p>
        <p>PINs can only be created or reset in person.</p>
        <p>You can still call 800-217-0049 to talk with a representative about your offers.</p>
      </peppermill-content-block>
    </template>

    <passkey-nudge></passkey-nudge>

    <account-protection-checklist></account-protection-checklist>

    <peppermill-content-block is-big-link-container>
      <peppermill-big-link path="/offers" title="Offers" icon="sell"></peppermill-big-link>
      <peppermill-big-link path="/info" title="Account" icon="user_attributes"></peppermill-big-link>
      <peppermill-big-link path="/win-loss" title="Win/Loss Statements" icon="print"></peppermill-big-link>
      <peppermill-big-link path="/logout" title="Logout" icon="logout"></peppermill-big-link>
    </peppermill-content-block>

    <peppermill-dialog
      title="Tier Points & Comp Dollars"
      is-alert
      :is-active="isPointDetailsDialogOpen"
      @close="isPointDetailsDialogOpen = false"
    >
      <p>Tier Point and Comp Dollar amounts include linked account data.</p>
    </peppermill-dialog>
  </peppermill-viewport>
</template>

<style scoped>
.account_details--container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 1em;
  justify-items: center;
}

@media (max-width: 800px) {
  .account_details--container {
    grid-template-columns: 1fr;
  }
}

.account_details--container + .layout--content_block {
  margin-top: 4em;
}

.account_details--image {
  position: relative;
  display: inline-block;
  justify-self: center;
}

.account_details--image img {
  width: 300px;
}

.account_details--name {
  position: absolute;
  bottom: 10%;
  left: 50%;
  transform: translateX(-50%);
  color: #000;
  font-size: 1.1em;
  font-weight: bold;
  white-space: nowrap;
  text-align: center;
}

.account_details--name-large {
  bottom: 15%;
  font-size: 1.2em;
}

.account-details--info {
  display: grid;
  grid-gap: 0.5em;
  grid-column: 1 / -1;
  text-align: center;
}

.account-details--info span {
  position: relative;
}

.account-details--info .material-icons {
  position: absolute;
  top: 50%;
  left: calc(100% + 0.25em);
  font-size: 1.2em;
  text-decoration: none;
  color: rgb(var(--primary-color));
  transform: translateY(-50%);
}

.account_details--progress_bar {
  --percent-through-tier: 0%;
  position: relative;
  width: 100%;
  height: 35px;
  background: rgba(255, 255, 255, 0.85); /* #EEE; */ /* #df1a22; */
  margin: 5px 0;
  overflow: hidden;
}

.account_details--progress_bar_text {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 35px;
  line-height: 35px;
  vertical-align: middle;
  text-align: center;
  z-index: 10;
  font-size: 1.2em;
}

.account_details--progress_bar_fill {
  position: absolute;
  top: 0;
  left: 0;
  width: var(--percent-through-tier);
  height: 100%;
  background: #b01117;
  opacity: 0.25;

  transform-origin: 0% 50%;
  transform: scale(0, 1);
  transition:
    opacity 1250ms ease-in-out,
    transform 2500ms ease-in-out;
}

.account_details--container-loaded .account_details--progress_bar_fill {
  opacity: 1;
  transform: none;
}

.account_details--progress_bar-bronze .account_details--progress_bar_fill {
  background-color: #d7a023;
}

.account_details--progress_bar-gold .account_details--progress_bar_fill {
  background-color: #858587;
}

.account_details--progress_bar-platinum .account_details--progress_bar_fill {
  background-color: #c4ccd0;
}

.account_details--progress_bar-diamond .account_details--progress_bar_fill {
  background-color: #888a8c;
}

.account_details--progress_bar-chairman .account_details--progress_bar_fill {
  background-color: #000;
}
</style>
